import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { QuestionIcon } from "@phosphor-icons/react";

import Tooltip from "../../../../../components/common/tooltip/Tooltip";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import { getDimensionDotColor, type GapAnalysisDimension, TONE_COLORS } from "./gapAnalysisConstants";

interface DimensionCellProps {
  dimension: GapAnalysisDimension;
  isFirstColumn: boolean;
  isCompact: boolean;
  isSelected: boolean;
  onSelectDimension?: (dimensionId: string) => void;
}

export function DimensionCell({
  dimension,
  isFirstColumn,
  isCompact,
  onSelectDimension,
  isSelected,
}: DimensionCellProps) {
  const toneColor = TONE_COLORS[dimension.tone];
  const dotColor = getDimensionDotColor(dimension);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${dimension.label}, ${dimension.informational ? "informational only, " : ""}${
        dimension.issues
      } ${dimension.issues === 1 ? "item" : "items"}`}
      accessibilityState={{ selected: isSelected }}
      onPress={() => onSelectDimension?.(dimension.id)}
      style={[styles.dimensionCell, !isCompact && !isFirstColumn && styles.dimensionCellWithBorder]}>
      {isSelected && <View style={[styles.selectedAccent, { backgroundColor: toneColor }]} />}
      <View style={styles.dimensionTopRow}>
        <View style={[styles.dimensionDot, { backgroundColor: dotColor }]} />
        <View style={styles.dimensionLabelGroup}>
          <CustomText
            text={dimension.label}
            useTranslationText={false}
            size="s"
            weight="medium"
            numberOfLines={1}
            style={styles.dimensionLabel}
          />
          <Tooltip
            content={dimension.tooltip}
            maxWidth={320}
            offset={8}
            showArrow={false}
            textNumberOfLines={6}
            tooltipStyle={styles.dimensionTooltip}
            tooltipTextStyle={styles.dimensionTooltipText}
            wrapText={true}>
            <View
              accessibilityLabel={`What ${dimension.label} checks: ${dimension.tooltip}`}
              accessibilityRole="image"
              style={styles.dimensionHelpIcon}>
              <QuestionIcon size={14} color={colors.grey[400]} weight="bold" />
            </View>
          </Tooltip>
        </View>
        <View style={styles.dimensionScoreGroup}>{dimension.informational && <InformationalBadge />}</View>
      </View>
      {dimension.summary && (
        <CustomText
          text={dimension.summary}
          useTranslationText={false}
          size="xs"
          numberOfLines={3}
          style={styles.dimensionSummary}
        />
      )}
    </Pressable>
  );
}

export function InformationalBadge() {
  return (
    <View
      accessibilityLabel="Informational. Shown for context - does not block publish"
      style={styles.informationalBadge}>
      <CustomText
        text="Informational"
        useTranslationText={true}
        size="xs"
        weight="medium"
        style={styles.informationalText}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  dimensionCell: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: colors.white,
    position: "relative",
  },
  dimensionCellWithBorder: {
    borderLeftWidth: 1,
    borderLeftColor: colors.grey[200],
  },
  dimensionTopRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  dimensionDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    flexShrink: 0,
  },
  dimensionLabelGroup: {
    minWidth: 0,
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  dimensionLabel: {
    color: colors.grey[900],
    flexShrink: 1,
  },
  dimensionHelpIcon: {
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  dimensionTooltip: {
    backgroundColor: colors.grey[900],
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    shadowColor: colors.grey[900],
    shadowOpacity: 0.18,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 3 },
  },
  dimensionTooltipText: {
    color: colors.white,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "600",
    textAlign: "left",
  },
  informationalBadge: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.grey[100],
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    flexShrink: 0,
  },
  informationalText: {
    color: colors.grey[600],
    fontSize: 10,
    lineHeight: 12,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  dimensionScoreGroup: {
    marginLeft: "auto",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  dimensionSummary: {
    color: colors.grey[500],
    marginTop: 8,
    lineHeight: 16,
  },
  selectedAccent: {
    position: "absolute",
    left: 0,
    top: 8,
    bottom: 8,
    width: 2,
    borderTopRightRadius: 999,
    borderBottomRightRadius: 999,
  },
});
